/**
 * Wards and sanitised beds for a tablet driving its robot directly: from the offline pack (as of
 * its last sync), plus every cycle completed on this tablet that is still waiting to upload. Works
 * the same with or without data.
 */
import type { SyncCycle } from '@evesix/contracts';
import { ROBOT_POSITIONS } from '../domain/catalog';
import { formatLastCycle } from '../domain/format';
import type { CatalogSnapshot, Ward } from '../domain/types';
import type { CatalogService } from './catalog';
import type { DirectRobotGateway } from './direct-robot';
import type { LocalStore } from './local-store';
import type { SyncService } from './sync';

export class OfflineCatalogService implements CatalogService {
  readonly #sync: SyncService;
  readonly #store: LocalStore;
  readonly #robot: DirectRobotGateway;
  readonly #now: () => number;

  constructor(
    sync: SyncService,
    store: LocalStore,
    robot: DirectRobotGateway,
    now: () => number = Date.now,
  ) {
    this.#sync = sync;
    this.#store = store;
    this.#robot = robot;
    this.#now = now;
  }

  async load(): Promise<CatalogSnapshot> {
    await Promise.all([this.#sync.ready, this.#robot.ready]);
    void this.#sync.syncNow(); // fresher numbers next time; never waited for
    const pack = this.#sync.pack;
    if (!pack) {
      throw new Error('This tablet has no offline data yet: sign in once with a service code');
    }

    const now = this.#now();
    const windowMs = pack.sanitisedWindowSeconds * 1000;
    // Service accounts' test runs: the server will not count them as cleaning a room either.
    const testers = new Set(
      pack.operators.filter((o) => o.service && !o.mayRunRealCycles).map((o) => o.id),
    );
    const local = (await this.#store.outbox())
      .filter((e) => e.kind === 'cycle' && e.state === 'pending')
      .map((e) => e.body as unknown as SyncCycle)
      .filter((c) => c.status === 'completed' && !testers.has(c.operatorId));
    // Cleaned within the window, as of now: the tablet ages beds out itself, with or without data.
    const sanitised = new Set(
      Object.entries(pack.sanitised)
        .filter(([, at]) => now - Date.parse(at) < windowMs)
        .map(([bed]) => bed),
    );
    const lastByWard = new Map<string, string>();
    for (const cycle of local) {
      if (now - Date.parse(cycle.finishedAt) < windowMs)
        sanitised.add(`${cycle.wardCode}-${cycle.bed}`);
      const known = lastByWard.get(cycle.wardCode);
      if (!known || known < cycle.finishedAt) lastByWard.set(cycle.wardCode, cycle.finishedAt);
    }

    const wards: Ward[] = pack.wards.map((w) => {
      const localLast = lastByWard.get(w.code) ?? null;
      const last =
        !w.lastCycleAt || (localLast && localLast > w.lastCycleAt) ? localLast : w.lastCycleAt;
      return { code: w.code, name: w.name, beds: w.beds, lastCycle: formatLastCycle(last, now) };
    });

    const run = this.#robot.activeRun();
    const ward = run && wards.find((w) => w.code === run.job.wardCode);
    return {
      wards,
      sanitisedBeds: [...sanitised].sort(),
      lampChannels: pack.robot.outputs,
      activeCycle:
        run && ward
          ? {
              cycleId: run.id,
              ward,
              bed: run.job.bed,
              position: ROBOT_POSITIONS.find((p) => p.id === run.job.positionId) ?? {
                id: run.job.positionId,
                label: run.job.positionLabel,
                x: 50,
                y: 50,
              },
              durationSeconds: run.job.durationSeconds,
              wallClockSeconds: run.job.wallClockSeconds,
              channels: run.job.channels,
              startedAt: Date.parse(run.startedAt),
            }
          : null,
    };
  }
}
